/**
 * Epic board configuration: which orchestrator state lands in which swimlane.
 *
 * Pure + dependency-free. To change the board, edit LANES / BACKLOG_STATES only —
 * the UI is derived from this file.
 */

export type LaneId =
  | 'requirements'
  | 'planning'
  | 'building'
  | 'deploy'
  | 'review'
  | 'done'
  | 'blocked';

export interface LaneDef {
  id: LaneId;
  label: string;
  hint: string;
  /** Tailwind classes for the lane's top accent bar. */
  accent: string;
  states: readonly string[];
}

export const LANES: readonly LaneDef[] = [
  {
    id: 'requirements',
    label: 'Requirements',
    hint: 'Refreshing the code graph, reviewing requirements, waiting on answers or target confirmation',
    accent: 'bg-violet-400',
    states: [
      'GRAPH_REFRESHING',
      'REQUIREMENTS_REVIEW',
      'REQUIREMENTS_QUESTIONS',
      'REQUIREMENTS_QUESTIONS_IN_PROGRESS',
      'REQUIREMENTS_REPLY_RECEIVED',
      'TARGETS_PENDING',
      // Parity-before analysis + repo /init scan (pre-build understanding work)
      'PARITY_SCOPING',
      'PARITY_SCOPE_QUESTIONS',
      'PARITY_UNDERSTANDING_RUNNING',
      'PARITY_UNDERSTANDING_POSTED',
      'PARITY_UNDERSTANDING_REVISION',
      'PARITY_TESTS_AUTHORING',
      'PARITY_BASELINE_RUNNING',
      'INIT_RUNNING',
      'INIT_SCANNED',
      'INIT_GRAPH_REFRESHING',
      'INIT_ANALYZING',
      'INIT_QUESTIONS',
      'INIT_QUESTIONS_IN_PROGRESS',
      'INIT_REPLY_RECEIVED',
      'INIT_ENRICHED',
    ],
  },
  {
    id: 'blocked',
    label: 'Blocked',
    hint: 'Failed, aborted, stuck behind a gate, or in a state the board does not recognise. Archive to clear.',
    accent: 'bg-rose-500',
    states: [
      'FAILED',
      'ABANDONED',
      'ABORTED',
      'INIT_FAILED',
      'PARITY_FAILED',
      'PARITY_RUN_FAILED',
      'PARITY_GATE_BLOCKED',
    ],
  },
  {
    id: 'planning',
    label: 'Planning',
    hint: 'Requirements aligned; epic plan being drafted, validated or approved',
    accent: 'bg-sky-400',
    states: [
      'REQUIREMENTS_ALIGNED',
      // Parity-before analysis finished (scope confirmed / understanding approved / baseline ready):
      // waiting on the next phase, not gathering requirements any more.
      'PARITY_SCOPE_CONFIRMED',
      'PARITY_UNDERSTANDING_APPROVED',
      'PARITY_BASELINE_READY',
      'PLANNING',
      'PLAN_QUESTIONS',
      'PLAN_QUESTIONS_IN_PROGRESS',
      'EPIC_VALIDATING',
      'PLAN_POSTED',
    ],
  },
  {
    id: 'building',
    label: 'Building',
    hint: 'Agents are implementing stories',
    accent: 'bg-primary-500',
    states: ['IN_PROGRESS', 'BUILDING', 'BUGFIX_IN_PROGRESS', 'READY_FOR_DEPLOY'],
  },
  {
    id: 'deploy',
    label: 'Deploy & Test',
    hint: 'Deploying to dev/test and running post-deploy checks (incl. rollbacks)',
    accent: 'bg-amber-400',
    states: [
      'DEPLOYING_DEV',
      'TESTING_DEV',
      'DEPLOYING_TEST',
      'TESTING_TEST',
      'PARITY_VERIFY_QUEUED',
      'PARITY_VERIFYING',
      'PARITY_VERIFIED',
      'ROLLBACK_IN_PROGRESS',
      'ROLLED_BACK',
    ],
  },
  {
    id: 'review',
    label: 'In Review',
    hint: 'PR is open and waiting for a human',
    accent: 'bg-teal-400',
    states: ['READY_FOR_REVIEW', 'READY_FOR_REVIEW_NOTIFIED'],
  },
  {
    id: 'done',
    label: 'Done',
    hint: 'Completed. Auto-archived after 7 days (use "Show archived" to see older ones)',
    accent: 'bg-emerald-500',
    states: ['COMPLETE', 'COMPLETED', 'BUGFIX_COMPLETE', 'INIT_COMPLETE'],
  },
];

/** Not picked up yet — lives on the Backlog tab, not the board. */
export const BACKLOG_STATES: ReadonlySet<string> = new Set(['DEFINED', 'BUGFIX_DEFINED', 'INIT_DEFINED']);

/** States where Forge is waiting on a human; cards get an amber "needs you" accent. */
export const NEEDS_HUMAN_STATES: ReadonlySet<string> = new Set([
  'REQUIREMENTS_QUESTIONS',
  'PLAN_QUESTIONS',
  'TARGETS_PENDING',
  'PARITY_SCOPE_QUESTIONS',
  'PARITY_UNDERSTANDING_POSTED',
  'INIT_QUESTIONS',
  'PLAN_POSTED',
  'READY_FOR_REVIEW',
  'READY_FOR_REVIEW_NOTIFIED',
]);

const STATE_TO_LANE: ReadonlyMap<string, LaneId> = new Map(
  LANES.flatMap((l) => l.states.map((s) => [s, l.id] as const)),
);

export type Placement = LaneId | 'backlog';

/** Where an epic with this state belongs. Unknown states go to 'blocked' so nothing is silently dropped. */
export function placementForState(state: string): Placement {
  const s = String(state ?? '').toUpperCase();
  if (BACKLOG_STATES.has(s)) return 'backlog';
  const lane = STATE_TO_LANE.get(s);
  if (lane) return lane;
  return 'blocked';
}

/** Short repo name ("org/repo" -> "repo"). */
export function shortRepo(repo: string): string {
  const i = repo.lastIndexOf('/');
  return i >= 0 ? repo.slice(i + 1) : repo;
}

const PROJECT_PALETTE = [
  { chip: 'bg-rose-50 text-rose-700 border-rose-200', bar: 'bg-rose-400' },
  { chip: 'bg-orange-50 text-orange-700 border-orange-200', bar: 'bg-orange-400' },
  { chip: 'bg-lime-50 text-lime-700 border-lime-200', bar: 'bg-lime-500' },
  { chip: 'bg-emerald-50 text-emerald-700 border-emerald-200', bar: 'bg-emerald-400' },
  { chip: 'bg-cyan-50 text-cyan-700 border-cyan-200', bar: 'bg-cyan-400' },
  { chip: 'bg-blue-50 text-blue-700 border-blue-200', bar: 'bg-blue-400' },
  { chip: 'bg-violet-50 text-violet-700 border-violet-200', bar: 'bg-violet-400' },
  { chip: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200', bar: 'bg-fuchsia-400' },
] as const;

/** Stable colour per repo/project key (same key -> same colour on every render and every page). */
export function projectColor(key: string): { chip: string; bar: string } {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return PROJECT_PALETTE[h % PROJECT_PALETTE.length];
}
